import type { SyncStatus } from '../../offline-mode/utils/consts';

export enum NetworkType {
  '2g' = '2g',
  '3g' = '3g',
  '4g' = '4g',
  '5g' = '5g',
  'Wifi' = 'Wifi',
  'unknown' = 'unknown',
}

export enum BiometricType {
  TouchID = 'touchId',
  FaceID = 'faceId',
  Biometrics = 'biometrics',
}

export enum GeoLocationStatus {
  Granted = 'GRANTED',
  Disabled = 'DISABLED',
  UserDenied = 'USER_DENIED',
}

export type UserExtraGeoLocationContext = {
  isMocked?: boolean;
  status?: GeoLocationStatus | null;
  isLocationServiceEnabled?: boolean;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

export type UserExtraPermissionsContext = {
  // location authorization moved here from geography. `allowed` is deprecated
  // (hidden from the builder schema) but still populated at runtime
  // (= !isNotAllowed) so interfaces bound to it before the move keep resolving.
  location: {
    allowed?: boolean;
    isBgLocationAllowed?: boolean;
    isWhenInUseAllowed?: boolean;
    isNotAllowed?: boolean;
  };
  camera: { allowed: boolean };
  microphone: { allowed: boolean };
  pushNotifications: { allowed: boolean };
};

export type OfflineModeContext = {
  isReady: boolean;
  status: SyncStatus;
  lastSyncTimestamp: string | null;
  isSyncInProgress: boolean;
  isSyncComplete: boolean;
  isSyncError: boolean;
};

export type NetworkContext = {
  isOffline: boolean;
  networkType: NetworkType;
};

export type UAEPassContext = {
  accessCode: string;
  redirectUri: string;
  error?: {
    code: string;
    message: string;
  };
};

export type RazorpayContext = {
  paymentId?: string;
  orderId?: string;
  signature?: string;
  error?: {
    code: string;
    description: string;
    reason: string;
  };
};

export type MobileUserExtraContext = {
  buildInfo: {
    deploymentVersion: string;
    runtimeVersion: string;
    buildVersion: string;
    otaUpdateId: string | undefined;
    otaShortUpdateId: string | undefined;
    otaUpdateCreatedAt: number | undefined;
    otaUpdateInfo?: {
      isUpdateAvailable: boolean | undefined;
      updateDownloading: boolean | undefined;
      updateDownloadError: boolean | undefined;
      updateDownloadSuccess: boolean | undefined;
    };
  };
  safeArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  biometric: {
    available: boolean;
    biometryType: BiometricType;
  };
  device: {
    deviceId: string;
    isIOS: boolean;
    isAndroid: boolean;
    isRealDevice: boolean;
    isRooted: boolean;
    isDeveloperModeEnabled: boolean;
    isAdbEnabled: boolean;
    isBatterySaverEnabled: boolean;
    isClonedApp: boolean;
    isVirtualizedApp: boolean;
    brand: string;
    dimensions: { width: number; height: number; fontScale: number };
    os: { name: string; version: string };
    modelName: string;
    modelId: string;
    isLowEndDevice?: boolean;
  };
  network: NetworkContext;
  geography?: UserExtraGeoLocationContext;
  permissions?: UserExtraPermissionsContext;
  offlineMode?: OfflineModeContext;
};

export type WebUserExtraContext = {
  device: {
    deviceId?: string;
    brand: string;
    dimensions: { width: number; height: number };
    os: { name: string; version: string };
    modelName: string;
    modelId: string;
    isLowEndDevice?: boolean;
  };
  browser: { name: string; version: string };
  network: NetworkContext;
  geography?: UserExtraGeoLocationContext;
};

export type UserExtraContext = MobileUserExtraContext | WebUserExtraContext;

export type UserRuntimeEnvDetails = UserExtraContext | undefined;

/**
 * Canonical runtime-env shape of the first-class `device` store slice / compute-context
 * entry: `deviceInfo` is the legacy inner `device`, `location` the legacy `geography`.
 * The legacy `UserExtraContext` shape above stays for the server exchange
 * (fetchUserDetails / UserStore) and for the derived `userContext.context` view that
 * keeps old bindings resolving.
 */
export type MobileDeviceContext = Omit<MobileUserExtraContext, 'device' | 'geography'> & {
  deviceInfo: MobileUserExtraContext['device'];
  location?: UserExtraGeoLocationContext;
};

export type WebDeviceContext = Omit<WebUserExtraContext, 'device' | 'geography'> & {
  deviceInfo: WebUserExtraContext['device'];
  location?: UserExtraGeoLocationContext;
};

export type DeviceContext = MobileDeviceContext | WebDeviceContext;
